import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, Phone, ShieldCheck, MapPin, Siren, Tag } from "lucide-react";

import { Img } from "../../lib/img";
import { BUSINESS, IMAGES, PLANS, SPECIALS } from "../../content/site";
import { ReviewsWidget } from "./ReviewsWidget";
import { lazy, Suspense } from "react";
const LeadForm = lazy(() => import("./LeadForm").then((m) => ({ default: m.LeadForm })));

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);

export const SectionHead = ({ eyebrow, title, text, light = false, center = false }) => (
  <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
    {eyebrow && <p className={`eyebrow ${light ? "text-gold" : ""}`}><span className="h-0.5 w-6 bg-gold" aria-hidden="true" />{eyebrow}</p>}
    <h2 className={`h2 mt-3 ${light ? "text-white" : ""}`}>{title}</h2>
    {text && <p className={`mt-4 text-base leading-relaxed md:text-lg ${light ? "text-white/80" : "text-ink-soft"}`}>{text}</p>}
  </div>
);

export const ServiceCard = ({ name, path, image, alt, text }) => (
  // Phones: compact row (thumbnail + text), whole card tappable. Tablet/desktop: image-on-top card.
  <article className="group relative flex overflow-hidden rounded-2xl border border-gray-100 bg-white transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl sm:flex-col" data-testid={`service-card-${slug(name)}`}>
    <div className="relative w-28 shrink-0 overflow-hidden bg-plum-soft sm:aspect-[16/10] sm:w-auto">
      <Img src={IMAGES[image]} alt={alt || name} widths={[360, 540, 720, 900]} sizes="(min-width:1024px) 300px, (min-width:640px) 50vw, 112px" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
    </div>
    <div className="flex flex-1 flex-col p-4 sm:p-6">
      <h3 className="text-lg font-bold text-ink sm:text-xl">{name}</h3>
      <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-ink-soft sm:mt-2 sm:line-clamp-none sm:flex-1 sm:text-[15px]">{text}</p>
      <Link to={path} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-plum transition-[gap] after:absolute after:inset-0 hover:gap-2.5 sm:mt-5 sm:text-base" data-testid={`service-card-link-${slug(name)}`}>
        Learn More<span className="sr-only"> about {name}</span> <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  </article>
);

export const EmergencyBanner = () => (
  <section className="bg-gold" aria-labelledby="emergency-banner-title" data-testid="emergency-banner">
    <div className="container-x flex flex-col items-start justify-between gap-6 py-10 md:flex-row md:items-center">
      <div className="flex items-start gap-4">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-plum text-gold"><Siren className="h-7 w-7" aria-hidden="true" /></span>
        <div>
          <h2 id="emergency-banner-title" className="font-display text-2xl font-extrabold text-plum sm:text-3xl">Burst pipe? Sewer backup? No hot water?</h2>
          <p className="mt-1 text-plum/80">Shut off the water if you can, then call PlumbSmart for emergency plumbing in Asheville.</p>
        </div>
      </div>
      <div className="grid w-full gap-3 sm:flex sm:w-auto sm:flex-wrap">
        <a href={BUSINESS.phoneHref} className="btn-plum text-base" data-testid="emergency-banner-call-btn"><Phone className="h-4 w-4" aria-hidden="true" />Call {BUSINESS.phone}</a>
        <Link to="/emergency-plumbing" className="btn-outline bg-transparent" data-testid="emergency-banner-learn-btn">Emergency Plumbing</Link>
      </div>
    </div>
  </section>
);

export const PlansSection = ({ id = "plans" }) => (
  <section id={id} className="section bg-plum text-white" aria-labelledby={`${id}-title`} data-testid="plans-section">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <p className="eyebrow text-gold"><span className="h-0.5 w-6 bg-gold" aria-hidden="true" />Piper Protection Plans</p>
        <h2 id={`${id}-title`} className="h2 mt-3 text-white">Savings and priority care, all year long.</h2>
        <p className="mt-4 text-white/80">Annual plans built to help you save on routine service and maintenance, replacements and installations, and emergency plumbing needs, with free annual inspections depending on the plan.</p>
        <Img src={BUSINESS.piperBadge} alt="Piper Protection Plans badge with graduation cap on a yellow shield" widths={[240, 360]} sizes="180px" className="mt-8 hidden w-40 lg:block" />
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:col-span-8">
        {PLANS.map((p) => (
          <div key={p.name} className={`flex flex-col rounded-2xl p-7 ${p.featured ? "bg-white text-ink ring-4 ring-gold" : "bg-white/10 ring-1 ring-white/20"}`} data-testid={`plan-card-${slug(p.name)}`}>
            <h3 className={`text-xl font-bold ${p.featured ? "text-plum" : "text-white"}`}>{p.name}</h3>
            <p className="mt-4"><span className="font-display text-5xl font-extrabold">{p.price}</span><span className="opacity-70">{p.per}</span></p>
            <p className="text-sm opacity-70">{p.yearly}</p>
            <ul className="mt-6 flex-1 space-y-3 text-[15px]">
              {p.perks.map((k) => <li key={k} className="flex gap-3"><Check className={`mt-0.5 h-5 w-5 shrink-0 ${p.featured ? "text-plum" : "text-gold"}`} aria-hidden="true" />{k}</li>)}
            </ul>
            <Link to="/protection-plans" className={`${p.featured ? "btn-gold" : "btn-ghost-light"} mt-7`} data-testid={`plan-cta-${slug(p.name)}`}>View plan details</Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const SpecialsSection = () => (
  <section className="section bg-mist" aria-labelledby="specials-title" data-testid="specials-section">
    <div className="container-x">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead eyebrow="Current specials" title={<span id="specials-title">Specials &amp; promotions</span>} text="Ways PlumbSmart helps Asheville homeowners save on plumbing, drains, water heaters and filtration." />
        <Link to="/plumbsmart-specials" className="btn-outline shrink-0" data-testid="specials-view-all-btn">View all specials</Link>
      </div>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {SPECIALS.map((s, i) => (
          <div key={s.title} className={`rounded-2xl p-6 ${i === 0 ? "bg-plum text-white" : "bg-white"} border border-gray-100`} data-testid={`special-card-${i}`}>
            <Tag className={`h-6 w-6 ${i === 0 ? "text-gold" : "text-plum"}`} aria-hidden="true" />
            <h3 className={`mt-4 text-lg font-bold leading-snug ${i === 0 ? "text-white" : "text-ink"}`}>{s.title}</h3>
            <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-white/80" : "text-ink-soft"}`}>{s.text}</p>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-ink-soft">Offer terms and eligibility to be confirmed with PlumbSmart at booking.</p>
    </div>
  </section>
);

export const ReviewsSection = () => (
  <section id="reviews" className="section bg-white" aria-labelledby="reviews-title" data-testid="reviews-section">
    <div className="container-x">
      <SectionHead eyebrow="Reviews" title={<span id="reviews-title">What Asheville customers say</span>} text="Read what customers say about PlumbSmart on Google." />
      <ReviewsWidget />
      <a href={BUSINESS.bbb} target="_blank" rel="nofollow noopener noreferrer" className="mt-8 inline-flex items-center gap-2 font-semibold text-plum hover:underline" data-testid="reviews-bbb-link">
        <ShieldCheck className="h-5 w-5" aria-hidden="true" />View PlumbSmart on the Better Business Bureau
      </a>
    </div>
  </section>
);

export const AreasSection = ({ compact = false }) => (
  <section className={`${compact ? "py-14" : "section"} bg-plum-soft`} aria-labelledby="areas-title" data-testid="areas-section">
    <div className="container-x grid items-center gap-10 lg:grid-cols-2">
      <div>
        <p className="eyebrow"><span className="h-0.5 w-6 bg-gold" aria-hidden="true" />Areas we serve</p>
        <h2 id="areas-title" className="h2 mt-3">Asheville and surrounding Western North Carolina</h2>
        <p className="mt-4 leading-relaxed text-ink-soft">
          PlumbSmart has served homes and businesses across Asheville since {BUSINESS.since}, along with the surrounding Western North Carolina communities. Not sure if we cover your address? Give us a call and we'll let you know.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href={BUSINESS.phoneHref} className="btn-plum" data-testid="areas-call-btn"><Phone className="h-4 w-4" aria-hidden="true" />Check my address</a>
          <Link to="/schedule-service" className="btn-gold" data-testid="areas-schedule-btn">Schedule Service</Link>
        </div>
      </div>
      <div className="relative overflow-hidden rounded-3xl bg-plum p-8 text-white" data-testid="areas-hq-card">
        <svg viewBox="0 0 400 120" className="absolute inset-x-0 bottom-0 w-full text-white/10" aria-hidden="true"><path fill="currentColor" d="M0 120 L60 50 L110 90 L170 20 L230 80 L290 35 L350 85 L400 45 L400 120 Z" /></svg>
        <MapPin className="h-8 w-8 text-gold" aria-hidden="true" />
        <p className="mt-4 font-display text-2xl font-bold">Headquartered in Asheville, NC</p>
        <p className="mt-2 text-white/80">{BUSINESS.street}, {BUSINESS.cityLine}</p>
        <p className="relative mt-8 inline-block rounded-full bg-white/10 px-4 py-2 text-sm">Serving Asheville &amp; nearby WNC communities</p>
      </div>
    </div>
  </section>
);

// Native <details> keeps every answer in the HTML for search engines and works without JavaScript.
export const FaqList = ({ faqs, idPrefix = "faq" }) => (
  <div className="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white" data-testid={`${idPrefix}-accordion`}>
    {faqs.map((f, i) => (
      <details key={f.q} className="group px-4 sm:px-6" data-testid={`${idPrefix}-item-${i}`}>
        <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between gap-4 py-4 text-left sm:py-5 [&::-webkit-details-marker]:hidden" data-testid={`${idPrefix}-trigger-${i}`}>
          <h3 className="font-sans text-base font-semibold text-ink">{f.q}</h3>
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-soft transition-transform duration-200 group-open:rotate-180" aria-hidden="true" />
        </summary>
        <p className="pb-5 text-[15px] leading-relaxed text-ink-soft">{f.a}</p>
      </details>
    ))}
  </div>
);

export const ScheduleSection = ({ title = "Schedule service with PlumbSmart", text, defaultService, type = "service", id = "schedule" }) => (
  <section id={id} className="section relative overflow-hidden bg-plum-deep" aria-labelledby={`${id}-title`} data-testid="schedule-section">
    <div className="container-x relative grid gap-12 lg:grid-cols-12">
      <div className="text-white lg:col-span-5">
        <p className="eyebrow text-gold"><span className="h-0.5 w-6 bg-gold" aria-hidden="true" />{type === "water_heater_estimate" ? "Free estimate" : "Schedule service"}</p>
        <h2 id={`${id}-title`} className="h2 mt-3 text-white">{title}</h2>
        <p className="mt-4 text-white/80">{text || "Tell us what's going on and when works for you. We'll contact you to confirm the appointment."}</p>
        <ul className="mt-8 space-y-3 text-white/90">
          {["Set pricing and options on all repairs", "100% satisfaction guarantee", `Licensed & insured · ${BUSINESS.license}`].map((t) => (
            <li key={t} className="flex gap-3"><Check className="h-5 w-5 text-gold" aria-hidden="true" />{t}</li>
          ))}
        </ul>
        <a href={BUSINESS.phoneHref} className="mt-8 inline-flex min-h-[48px] items-center gap-3 font-display text-2xl font-bold text-gold" data-testid="schedule-section-call-link"><Phone className="h-6 w-6" aria-hidden="true" />{BUSINESS.phone}</a>
      </div>
      <div className="lg:col-span-7">
        <Suspense fallback={<div className="h-[560px] rounded-2xl bg-white" aria-busy="true" />}><LeadForm type={type} defaultService={defaultService} /></Suspense>
      </div>
    </div>
  </section>
);

// Lightweight closing CTA for reading pages (blog, FAQ): two links instead of the full form keeps markup lean.
export const CtaBand = ({ title = "Need a plumber in Asheville?", text = "Talk with the PlumbSmart office or request a time online. We'll contact you to confirm the appointment." }) => (
  <section id="schedule" className="section bg-plum-deep" aria-labelledby="cta-band-title" data-testid="cta-band">
    <div className="container-x flex flex-col gap-8 text-white lg:flex-row lg:items-center lg:justify-between">
      <div className="max-w-2xl">
        <h2 id="cta-band-title" className="h2 text-white">{title}</h2>
        <p className="mt-4 text-white/80">{text}</p>
      </div>
      <div className="grid gap-3 sm:flex">
        <a href={BUSINESS.phoneHref} className="btn-gold px-7 py-4 text-base" data-testid="cta-band-call-btn"><Phone className="h-5 w-5" aria-hidden="true" />Call {BUSINESS.phone}</a>
        <Link to="/schedule-service" className="btn-ghost-light px-7 py-4 text-base" data-testid="cta-band-schedule-btn">Schedule Service</Link>
      </div>
    </div>
  </section>
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="text-sm" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1.5 text-white/75">
      {items.map(([name, path], i) => (
        <li key={path} className="flex items-center gap-1.5">
          {i > 0 && <span aria-hidden="true">/</span>}
          {i === items.length - 1 ? <span aria-current="page" className="font-semibold text-white">{name}</span> : <Link to={path} className="inline-block py-2 hover:text-gold">{name}</Link>}
        </li>
      ))}
    </ol>
  </nav>
);

export const WarrantyCallout = () => (
  <div className="grid items-center gap-6 overflow-hidden rounded-3xl bg-gold-soft p-8 md:grid-cols-[1fr_auto]" data-testid="warranty-callout">
    <div>
      <p className="eyebrow">Water heater warranty</p>
      <p className="mt-2 font-display text-2xl font-bold text-ink">5 to 10 year warranties on PlumbSmart water heater installations</p>
      <p className="mt-2 text-ink-soft">Protection for one of your home's biggest plumbing investments. Ask about coverage when you request your free estimate.</p>
    </div>
    <div className="flex flex-wrap gap-3">
      <Link to="/free-water-heater-replacement" className="btn-gold" data-testid="warranty-estimate-btn">Free Water Heater Estimate</Link>
      <Link to="/warranties" className="btn-outline" data-testid="warranty-learn-btn">Warranty details</Link>
    </div>
  </div>
);

export const PlansCallout = () => (
  <div className="grid items-center gap-6 rounded-3xl bg-plum p-8 text-white md:grid-cols-[1fr_auto]" data-testid="plans-callout">
    <div>
      <p className="eyebrow text-gold">Piper Protection Plans</p>
      <p className="mt-2 font-display text-2xl font-bold">Save on service calls with a Piper Protection Plan</p>
      <p className="mt-2 text-white/80">Comprehensive from $14/month or Premium from $35/month, with discounts, priority scheduling and annual inspections.</p>
    </div>
    <Link to="/protection-plans" className="btn-gold" data-testid="plans-callout-btn">Compare plans</Link>
  </div>
);

export const FinancingCallout = () => (
  <div className="grid items-center gap-6 rounded-3xl border border-gray-200 bg-white p-8 md:grid-cols-[1fr_auto]" data-testid="financing-callout">
    <div>
      <p className="eyebrow">Financing</p>
      <p className="mt-2 font-display text-2xl font-bold text-ink">5, 7 or 10 year financing through GoodLeap</p>
      <p className="mt-2 text-ink-soft">Spread the cost of larger plumbing projects. Contact the office to find the plan that fits.</p>
    </div>
    <Link to="/payment-plans" className="btn-plum" data-testid="financing-callout-btn">Payment plans</Link>
  </div>
);
